'use client';

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BlockRoot, PortalLink, tx, useBlockPart } from '@akademi/site-builder-runtime';
import {
  ActivationCodeStep,
  TRADE_BUSINESS_TYPES,
  TRADE_FIELD_LIMITS,
  TradeDocumentsUpload,
  signInHrefFor,
  useTradeApplication,
  type GuestAccountDraft,
  type TradeDocumentType,
  type TradeDraftField,
  type TradeReferenceDraft,
} from '@commerce/site-builder-blocks';
import { useSolarText } from '../locales/useSolarText';
import { tradeSavingsText } from '../home/tradeSavings';
import { BASE_CSS, TradeStyles } from '../styles/TradeStyles';
import { APPLY_CSS } from '../styles/applyCss';

type Localized = string | Record<string, string>;

/** What a file input will take; the backend accepts the same three. */
const ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

/** The trade-body list's "Other" choice, which opens a text box. */
const OTHER_BODY = '__other__';

export interface SolarTradeApplicationProps {
  /** The accent eyebrow over the heading. */
  eyebrow?: Localized;
  title?: Localized;
  intro?: Localized;
  /**
   * What approval changes, as the aside's tick list. Authored, never defaulted: every line
   * is a claim about how this shop works, and a claim belongs in the seed data where the
   * assurance register can be checked against it — not hard-coded in a component.
   */
  approvalPoints?: Localized[];
  signInHref?: string;
  accountHref?: string;
  termsHref?: string;
  /** The section's id, so a hero's "Apply" button has somewhere to jump to. */
  anchor?: string;
}

/**
 * Allow Energy's trade application, in Horizon's form-first anatomy.
 *
 * <p><strong>Why this block exists at all.</strong> It is registered under the PLATFORM's
 * own block type, `TradeApplicationForm`, so the live trade page, the seed template and
 * `build-shell-and-home.py` keep working untouched — the theme simply wins the resolver
 * (`createBlockResolver` reads `themes[themeId].blocks[type]` before `defaultBlocks[type]`).
 * The shared block it replaces styles every field with INLINE style objects
 * (`account/accountShared.tsx`), and an inline style cannot be reached from a stylesheet, so
 * no amount of theme CSS could turn it into the approved mockup. The replacement is about
 * layout only.
 *
 * <p><strong>None of the logic is this block's.</strong> Whether the shop takes applications,
 * who is signed in, where their last application stands, what the shop requires, what is
 * missing, what request goes up, what happens to a guest who applies with their sign-up —
 * all of it is {@link useTradeApplication} in `@commerce/site-builder-blocks`, which the
 * shared block runs too. There is one implementation of applying for a trade account and
 * two layouts over it; a copy here would be a second validator drifting away from the
 * server's, found only when a shopper hits the difference.
 *
 * <p><strong>Where the words come from.</strong> The copy this page writes — the panel
 * titles, the field labels, the declaration, the aside — is this theme's, through
 * {@link useSolarText}. The platform's vocabulary is NOT re-translated: the business types,
 * the document types, the refusals, and what the shop asks of an applicant are read from
 * the commerce catalogue, which the storefront already loads and which the server's own
 * enums are named by. Two catalogues saying the same thing is how one of them goes stale.
 *
 * <p><strong>Content honesty.</strong> Nothing here promises a reply time, a stock position
 * or a delivery. The saving in the aside is the one the backend computed from this shop's
 * real RETAIL and TRADE prices and is absent when it sent none; the checklist beside it is
 * the shop's own requirements, read from the same call.
 *
 * <p><strong>It draws its own scope.</strong> This block is NOT in `SKINNED_BLOCKS` any
 * more — a skin that wrapped it would re-enter the resolver and find this block again — so
 * the `.ae` wrapper and the stylesheets that the skin used to provide are emitted here.
 * Without them the page would render with no tokens at all.
 */
export function SolarTradeApplication({
  eyebrow,
  title,
  intro,
  approvalPoints = [],
  signInHref = '/sign-in',
  accountHref = '/account',
  termsHref = '/terms',
  anchor = 'apply',
}: SolarTradeApplicationProps) {
  const t = useSolarText();
  /** The platform's own vocabulary: enum names, refusals, and what the shop asks for. */
  const { t: ct, i18n } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  // The flow, whole, from the blocks package — see the note above. `fieldLabel` makes the
  // "these are still needed" sentence name the labels this page actually shows.
  const {
    commerce,
    phase,
    viewer,
    requirements,
    needs,
    draft,
    setDraft,
    set,
    references,
    setReferences,
    documents,
    setDocuments,
    chosen,
    otherBody,
    setOtherBody,
    guest,
    setGuest,
    busy,
    error,
    sent,
    setSent,
    after,
    here,
    submit,
    afterSignedIn,
    pendingStatus,
  } = useTradeApplication({ fieldLabel: (field) => fieldName(t, field) });

  /**
   * Which of the two registration numbers the panel asks for. Presentation only: the draft
   * carries both `companyNumber` and `taxReference`, and the shared validator accepts either
   * one where the shop requires a registration. The mockup asks the question as a choice
   * between two legal forms rather than showing two boxes and letting the applicant guess,
   * so switching clears the other box — sending both would claim something untrue about one.
   */
  const [soleTrader, setSoleTrader] = useState(false);

  const names = (field: TradeDraftField): string => fieldName(t, field);
  const label = (field: TradeDraftField, required: boolean) =>
    required ? (
      names(field)
    ) : (
      <>
        {names(field)} <span className="ae-apply__opt">{t('tradeApply.optional')}</span>
      </>
    );
  const megabytes = Math.floor(requirements.maxBytes / (1024 * 1024));

  const accountLink = (
    <div className="ae-apply__links">
      <PortalLink href={accountHref}>{ct('commerce.trade.goToAccount')}</PortalLink>
    </div>
  );

  /** What the shop asks of an applicant, in the platform's own words — real requirements. */
  const asks: string[] = [
    ct('commerce.trade.checklist.business'),
    ...(needs('COMPANY_OR_TAX_REFERENCE') ? [ct('commerce.trade.checklist.companyOrUtr')] : []),
    ...(needs('VAT') ? [ct('commerce.trade.checklist.vat')] : []),
    ...(needs('TRADE_BODY') ? [ct('commerce.trade.checklist.tradeBody')] : []),
    ...(needs('REFERENCES') ? [ct('commerce.trade.checklist.references', { count: requirements.references })] : []),
    ...(needs('PROOF') ? [ct('commerce.trade.checklist.proof', { size: megabytes })] : []),
  ];

  const savings = requirements.savings
    ? tradeSavingsText({ typical: requirements.savings.typicalPct, upTo: requirements.savings.upToPct }, t)
    : null;

  const aside = (
    <aside className="ae-apply__aside">
      <div className="ae-apply__totals">
        <h2>{t('tradeApply.aside.title')}</h2>
        {savings ? <p className="ae-apply__saving">{savings}</p> : null}
        <ul className="ae-apply__asks">
          {asks.map((ask, index) => (
            <li key={index}>{ask}</li>
          ))}
        </ul>
        {approvalPoints.length ? (
          <ul className="ae-apply__ticks">
            {approvalPoints.map((point, index) => (
              <li key={index}>
                <Tick />
                {tx(point)}
              </li>
            ))}
          </ul>
        ) : null}
        <p className="ae-apply__note">{ct('commerce.trade.checklist.review')}</p>
        <p className="ae-apply__note ae-apply__inline">
          <strong>{t('tradeApply.aside.alreadyApproved')}</strong>{' '}
          <PortalLink href={signInHrefFor(signInHref, here)}>{t('tradeApply.aside.signIn')}</PortalLink>{' '}
          {t('tradeApply.aside.signInText')}
        </p>
      </div>
    </aside>
  );

  // ── What shows instead of the form ───────────────────────────────────────────────────
  let instead: ReactNode = null;
  if (!commerce?.submitTradeApplication) {
    // A host whose commerce source cannot take applications. The shared block shows the
    // platform's dev placeholder here; a shop page says the plain thing instead.
    instead = (
      <p role="status" className="ae-apply__muted">
        {t('tradeApply.unavailable')}
      </p>
    );
  } else if (phase.kind === 'checking') {
    instead = <p className="ae-apply__muted">{t('tradeApply.loading')}</p>;
  } else if (phase.kind === 'off') {
    instead = (
      <p role="status" className="ae-apply__muted">
        {t('tradeApply.unavailable')}
      </p>
    );
  } else if (sent && sent.status !== 'REJECTED') {
    instead = (
      <>
        <p role="status" className="ae-apply__notice">
          {sent.status === 'APPROVED' ? ct('commerce.trade.approved') : ct('commerce.trade.sent', { email: sent.email })}
        </p>
        {accountLink}
      </>
    );
  } else if (after.kind === 'activate') {
    instead = (
      <ActivationCodeStep
        commerce={commerce}
        email={after.email}
        password={after.password}
        idPrefix="ae-trade"
        signInHref={signInHref}
        here={here}
        onSignedIn={() => void afterSignedIn(after.email)}
      />
    );
  } else if (after.kind === 'documents') {
    const { email } = after;
    instead = (
      <TradeDocumentsUpload
        commerce={commerce}
        requirements={requirements}
        initial={chosen}
        outstanding={after.status?.documentsOutstanding === true}
        onDone={(status) => setSent(status)}
        onSkip={() => setSent(after.status ?? pendingStatus(email))}
      />
    );
  } else if (after.kind === 'signIn') {
    instead = (
      <>
        <p role="status" className="ae-apply__notice">
          {ct('commerce.signUpApply.activate.readySignIn')}
        </p>
        <div className="ae-apply__links">
          <PortalLink href={signInHrefFor(signInHref, accountHref)}>{ct('commerce.account.signIn')}</PortalLink>
        </div>
      </>
    );
  } else if (phase.existing?.status === 'PENDING') {
    instead = (
      <>
        <p role="status" className="ae-apply__notice">
          {ct('commerce.trade.pending', {
            businessName: phase.existing.businessName,
            date: shortDate(phase.existing.submittedAt, i18n?.language),
            email: phase.existing.email,
          })}
        </p>
        {accountLink}
      </>
    );
  } else if (phase.existing?.status === 'APPROVED') {
    instead = (
      <>
        <p role="status" className="ae-apply__notice">
          {ct('commerce.trade.approved')}
        </p>
        {accountLink}
      </>
    );
  }

  const rejected = phase.kind === 'ready' && phase.existing?.status === 'REJECTED' ? phase.existing : null;

  // ── The form ─────────────────────────────────────────────────────────────────────────
  const limit = (field: keyof typeof TRADE_FIELD_LIMITS) => TRADE_FIELD_LIMITS[field];
  const guestSet = (field: keyof GuestAccountDraft) => (value: string) =>
    setGuest((current) => ({ ...current, [field]: value }));

  function addFiles(list: FileList | null) {
    if (!list || list.length === 0) return;
    const only = requirements.documentTypes.length === 1 ? (requirements.documentTypes[0] ?? '') : '';
    setDocuments((current) => {
      let next = Math.max(0, ...current.map((row) => row.key)) + 1;
      const rows = current.filter((row) => row.file);
      for (const file of Array.from(list)) rows.push({ key: next++, type: only, file });
      return rows;
    });
  }

  const form = (
    <form className="ae-apply__form" noValidate onSubmit={(event) => void submit(event)}>
      {/* 01 — Your business */}
      <section className="ae-apply__panel">
        <h3>
          <span className="ae-apply__num" aria-hidden="true">
            01
          </span>
          {t('tradeApply.panels.business')}
        </h3>
        <div className="ae-apply__grid">
          <Field id="ae-trade-business" className="ae-apply__span2" label={label('businessName', true)}>
            <input id="ae-trade-business" type="text" autoComplete="organization" maxLength={limit('businessName')} value={draft.businessName} onChange={(event) => set('businessName')(event.target.value)} />
          </Field>
          <Field id="ae-trade-type" label={label('businessType', true)}>
            <select
              id="ae-trade-type"
              value={draft.businessType}
              onChange={(event) => set('businessType')(event.target.value)}
            >
              <option value="">{ct('commerce.trade.chooseBusinessType')}</option>
              {TRADE_BUSINESS_TYPES.map((type) => (
                <option key={type} value={type}>
                  {ct(`commerce.trade.businessTypes.${type}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field id="ae-trade-website" label={label('website', false)}>
            <input id="ae-trade-website" type="url" autoComplete="url" maxLength={limit('website')} value={draft.website} onChange={(event) => set('website')(event.target.value)} />
          </Field>
        </div>

        <fieldset className="ae-apply__group">
          <legend>{t('tradeApply.legal.legend')}</legend>
          <Tile
            name="ae-trade-legal"
            checked={!soleTrader}
            onChange={() => {
              setSoleTrader(false);
              set('taxReference')('');
            }}
            title={t('tradeApply.legal.company.title')}
            text={t('tradeApply.legal.company.text')}
          />
          <Tile
            name="ae-trade-legal"
            checked={soleTrader}
            onChange={() => {
              setSoleTrader(true);
              set('companyNumber')('');
            }}
            title={t('tradeApply.legal.sole.title')}
            text={t('tradeApply.legal.sole.text')}
          />
        </fieldset>

        <div className="ae-apply__grid">
          {soleTrader ? (
            <Field
              id="ae-trade-utr"
              label={label('taxReference', needs('COMPANY_OR_TAX_REFERENCE'))}
              hint={t('tradeApply.hints.taxReference')}
            >
              <input id="ae-trade-utr" type="text" maxLength={limit('taxReference')} value={draft.taxReference} onChange={(event) => set('taxReference')(event.target.value)} />
            </Field>
          ) : (
            <Field
              id="ae-trade-company"
              label={label('companyNumber', needs('COMPANY_OR_TAX_REFERENCE'))}
              hint={t('tradeApply.hints.companyNumber')}
            >
              <input id="ae-trade-company" type="text" maxLength={limit('companyNumber')} value={draft.companyNumber} onChange={(event) => set('companyNumber')(event.target.value)} />
            </Field>
          )}
          {needs('VAT') ? null : (
            <Field id="ae-trade-vat" label={label('vatNumber', false)} hint={t('tradeApply.hints.vatNumber')}>
              <input
                id="ae-trade-vat"
                type="text"
                maxLength={limit('vatNumber')}
                value={draft.vatNumber}
                onChange={(event) => {
                  // The mockup asks the question once: a number means registered, blank means
                  // not. The backend wants both facts, so the answer is kept in step here
                  // rather than asking twice for the same thing.
                  const value = event.target.value;
                  setDraft((current) => ({ ...current, vatNumber: value, vatRegistered: value.trim() ? 'yes' : 'no' }));
                }}
              />
            </Field>
          )}
          <Field id="ae-trade-address1" className="ae-apply__span2" label={label('addressLine1', true)}>
            <input id="ae-trade-address1" type="text" autoComplete="address-line1" maxLength={limit('addressLine1')} value={draft.addressLine1} onChange={(event) => set('addressLine1')(event.target.value)} />
          </Field>
          <Field id="ae-trade-address2" className="ae-apply__span2" label={label('addressLine2', false)}>
            <input id="ae-trade-address2" type="text" autoComplete="address-line2" maxLength={limit('addressLine2')} value={draft.addressLine2} onChange={(event) => set('addressLine2')(event.target.value)} />
          </Field>
          <Field id="ae-trade-city" label={label('city', true)}>
            <input id="ae-trade-city" type="text" autoComplete="address-level2" maxLength={limit('city')} value={draft.city} onChange={(event) => set('city')(event.target.value)} />
          </Field>
          <Field id="ae-trade-postcode" label={label('postcode', true)}>
            <input id="ae-trade-postcode" type="text" autoComplete="postal-code" maxLength={limit('postcode')} value={draft.postcode} onChange={(event) => set('postcode')(event.target.value)} />
          </Field>
        </div>

        {/* The shop asks the question explicitly where it requires an answer: deriving "not
            registered" from an empty box would answer on the applicant's behalf. */}
        {needs('VAT') ? (
          <fieldset className="ae-apply__group">
            <legend>{t('tradeApply.vat.legend')}</legend>
            <Tile
              name="ae-trade-vat-registered"
              checked={draft.vatRegistered === 'yes'}
              onChange={() => setDraft((current) => ({ ...current, vatRegistered: 'yes' }))}
              title={t('tradeApply.vat.yes')}
            />
            <Tile
              name="ae-trade-vat-registered"
              checked={draft.vatRegistered === 'no'}
              onChange={() => setDraft((current) => ({ ...current, vatRegistered: 'no', vatNumber: '' }))}
              title={t('tradeApply.vat.no')}
            />
            {draft.vatRegistered === 'yes' && (
              <Field id="ae-trade-vat-number" label={label('vatNumber', true)}>
                <input id="ae-trade-vat-number" type="text" maxLength={limit('vatNumber')} value={draft.vatNumber} onChange={(event) => set('vatNumber')(event.target.value)} />
              </Field>
            )}
          </fieldset>
        ) : null}

        {needs('TRADE_BODY') || requirements.tradeBodies.length > 0 || draft.tradeBody || draft.tradeBodyNumber ? (
          <fieldset className="ae-apply__group">
            <legend>{t('tradeApply.tradeBody.legend')}</legend>
            <div className="ae-apply__grid">
              {requirements.tradeBodies.length > 0 ? (
                <Field id="ae-trade-body-choice" label={label('tradeBody', needs('TRADE_BODY'))}>
                  <select
                    id="ae-trade-body-choice"
                    value={otherBody ? OTHER_BODY : draft.tradeBody}
                    onChange={(event) => {
                      const value = event.target.value;
                      setOtherBody(value === OTHER_BODY);
                      set('tradeBody')(value === OTHER_BODY ? '' : value);
                    }}
                  >
                    <option value="">{ct('commerce.trade.chooseTradeBody')}</option>
                    {requirements.tradeBodies.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                    <option value={OTHER_BODY}>{ct('commerce.trade.otherTradeBody')}</option>
                  </select>
                </Field>
              ) : null}
              {requirements.tradeBodies.length === 0 || otherBody ? (
                <Field
                  id="ae-trade-body"
                  label={
                    requirements.tradeBodies.length === 0
                      ? label('tradeBody', needs('TRADE_BODY'))
                      : ct('commerce.trade.otherTradeBodyName')
                  }
                >
                  <input id="ae-trade-body" type="text" maxLength={limit('tradeBody')} value={draft.tradeBody} onChange={(event) => set('tradeBody')(event.target.value)} />
                </Field>
              ) : null}
              <Field id="ae-trade-body-number" label={label('tradeBodyNumber', needs('TRADE_BODY'))}>
                <input id="ae-trade-body-number" type="text" maxLength={limit('tradeBodyNumber')} value={draft.tradeBodyNumber} onChange={(event) => set('tradeBodyNumber')(event.target.value)} />
              </Field>
            </div>
          </fieldset>
        ) : null}
      </section>

      {/* 02 — Who to talk to */}
      <section className="ae-apply__panel">
        <h3>
          <span className="ae-apply__num" aria-hidden="true">
            02
          </span>
          {t('tradeApply.panels.contact')}
        </h3>
        {!viewer && (
          <fieldset className="ae-apply__group">
            <legend>{t('tradeApply.account.legend')}</legend>
            <p className="ae-apply__sub ae-apply__inline">
              {t('tradeApply.account.lead')}{' '}
              <PortalLink href={signInHrefFor(signInHref, here)}>{t('tradeApply.account.signIn')}</PortalLink>
            </p>
            <div className="ae-apply__grid">
              <Field id="ae-trade-first-name" label={t('tradeApply.account.firstName')}>
                <input id="ae-trade-first-name" type="text" autoComplete="given-name" maxLength={100} value={guest.firstName} onChange={(event) => guestSet('firstName')(event.target.value)} />
              </Field>
              <Field id="ae-trade-last-name" label={t('tradeApply.account.lastName')}>
                <input id="ae-trade-last-name" type="text" autoComplete="family-name" maxLength={100} value={guest.lastName} onChange={(event) => guestSet('lastName')(event.target.value)} />
              </Field>
              <Field
                id="ae-trade-account-email"
                className="ae-apply__span2"
                label={t('tradeApply.account.email')}
                hint={t('tradeApply.account.emailHint')}
              >
                <input id="ae-trade-account-email" type="email" autoComplete="email" maxLength={254} value={guest.email} onChange={(event) => guestSet('email')(event.target.value)} />
              </Field>
              <Field id="ae-trade-password" label={t('tradeApply.account.password')} hint={t('tradeApply.account.passwordHint')}>
                <input id="ae-trade-password" type="password" autoComplete="new-password" value={guest.password} onChange={(event) => guestSet('password')(event.target.value)} />
              </Field>
              <Field id="ae-trade-confirm-password" label={t('tradeApply.account.confirmPassword')}>
                <input id="ae-trade-confirm-password" type="password" autoComplete="new-password" value={guest.confirmPassword} onChange={(event) => guestSet('confirmPassword')(event.target.value)} />
              </Field>
            </div>
          </fieldset>
        )}
        <div className="ae-apply__grid">
          <Field id="ae-trade-contact" className="ae-apply__span2" label={label('contactName', true)}>
            <input id="ae-trade-contact" type="text" autoComplete="name" maxLength={limit('contactName')} value={draft.contactName} onChange={(event) => set('contactName')(event.target.value)} />
          </Field>
          <Field
            id="ae-trade-email"
            label={label('email', true)}
            hint={viewer ? t('tradeApply.hints.emailFromAccount') : t('tradeApply.hints.emailFromAbove')}
          >
            <input
              id="ae-trade-email"
              type="email"
              readOnly
              maxLength={limit('email')}
              value={viewer ? draft.email : guest.email.trim().toLowerCase()}
              onChange={(event) => set('email')(event.target.value)}
            />
          </Field>
          <Field id="ae-trade-phone" label={label('phone', true)}>
            <input id="ae-trade-phone" type="tel" autoComplete="tel" maxLength={limit('phone')} value={draft.phone} onChange={(event) => set('phone')(event.target.value)} />
          </Field>
          <Field id="ae-trade-message" className="ae-apply__span2" label={label('message', false)}>
            <textarea id="ae-trade-message" rows={3} maxLength={limit('message')} value={draft.message} onChange={(event) => set('message')(event.target.value)} />
          </Field>
        </div>
      </section>

      {/* 03 — Proof of trade */}
      <section className="ae-apply__panel">
        <h3>
          <span className="ae-apply__num" aria-hidden="true">
            03
          </span>
          {t('tradeApply.panels.proof')}
        </h3>
        {/* What the shop will accept as proof — the document types it actually returned, in
            the platform's own names, never a list written here. */}
        <p className="ae-apply__sub">{t('tradeApply.proof.lead')}</p>
        <ul className="ae-apply__asks">
          {requirements.documentTypes.map((type) => (
            <li key={type}>{ct(`commerce.trade.documentTypes.${type}`)}</li>
          ))}
        </ul>

        {references.length > 0 && (
          <fieldset className="ae-apply__group">
            <legend>{t('tradeApply.references.legend')}</legend>
            <p className="ae-apply__sub">{ct('commerce.trade.referencesNote', { count: requirements.references })}</p>
            {references.map((reference, index) => (
              <ReferenceFields
                key={index}
                index={index}
                value={reference}
                label={(field) => ct(`commerce.trade.reference.${field}`)}
                onChange={(next) => setReferences((current) => current.map((item, at) => (at === index ? next : item)))}
              />
            ))}
          </fieldset>
        )}

        <Field
          id="ae-trade-proof"
          label={label('documents', needs('PROOF'))}
          hint={t('tradeApply.hints.documents', { size: megabytes, max: requirements.maxDocuments })}
        >
          <input
            id="ae-trade-proof"
            type="file"
            multiple
            accept={ACCEPT}
            onChange={(event) => {
              addFiles(event.target.files);
              // Cleared so choosing the same file again still fires a change.
              event.target.value = '';
            }}
          />
        </Field>

        {documents.some((row) => row.file) && (
          <ul className="ae-apply__docs">
            {documents
              .filter((row) => row.file)
              .map((row) => (
                <li key={row.key} className="ae-apply__doc">
                  <span className="ae-apply__doc-name">{row.file?.name}</span>
                  <span className="ae-apply__doc-size">{kilobytes(row.file?.size ?? 0)}</span>
                  <div className="ae-apply__field">
                    <label htmlFor={`ae-trade-doc-type-${row.key}`}>{ct('commerce.trade.documentType')}</label>
                    <select
                      id={`ae-trade-doc-type-${row.key}`}
                      value={row.type}
                      onChange={(event) =>
                        setDocuments((current) =>
                          current.map((item) =>
                            item.key === row.key ? { ...item, type: event.target.value as TradeDocumentType | '' } : item,
                          ),
                        )
                      }
                    >
                      <option value="">{ct('commerce.trade.chooseDocumentType')}</option>
                      {requirements.documentTypes.map((type) => (
                        <option key={type} value={type}>
                          {ct(`commerce.trade.documentTypes.${type}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    className="ae-apply__drop"
                    onClick={() =>
                      setDocuments((current) => {
                        const rows = current.filter((item) => item.key !== row.key);
                        return rows.length ? rows : [{ key: 0, type: '', file: null }];
                      })
                    }
                  >
                    {ct('commerce.trade.removeDocument')}
                  </button>
                </li>
              ))}
          </ul>
        )}

        <label className="ae-apply__check">
          <input
            type="checkbox"
            checked={draft.declared}
            onChange={(event) => setDraft((current) => ({ ...current, declared: event.target.checked }))}
          />
          <span>
            {t('tradeApply.declaration')} {t('tradeApply.termsNotice')}{' '}
            <PortalLink href={termsHref}>{t('tradeApply.terms')}</PortalLink>.
          </span>
        </label>

        {error && (
          <div role="alert" className="ae-apply__error">
            {error.text}
            {error.kind === 'ALREADY_TRADE' && (
              <>
                {' '}
                <PortalLink href={accountHref}>{ct('commerce.trade.goToAccount')}</PortalLink>
              </>
            )}
          </div>
        )}

        <button type="submit" className="ae-apply__submit" disabled={busy}>
          {busy ? t('tradeApply.submitting') : viewer ? t('tradeApply.submit') : t('tradeApply.submitGuest')}
        </button>
      </section>
    </form>
  );

  return (
    <BlockRoot as="section" className="ae ae-apply" id={anchor || undefined}>
      <TradeStyles id="base" css={BASE_CSS} />
      <TradeStyles id="trade-apply" css={APPLY_CSS} />
      <div className="ae-apply__wrap">
        <div className="ae-apply__head">
          {/* The mockup's accent eyebrow. It is chrome, not a claim, so it has a default:
              the live page's authored props predate this block and carry none. */}
          <p className="ae-apply__eyebrow">{tx(eyebrow) || t('tradeApply.eyebrow')}</p>
          {/* An h2, not an h1: the page's own hero block carries the h1. */}
          <h2 {...part('title')} className="ae-apply__title">
            {tx(title) || t('tradeApply.title')}
          </h2>
          {tx(intro) ? (
            <p {...part('intro')} className="ae-apply__lede">
              {tx(intro)}
            </p>
          ) : null}
        </div>
        {instead ?? (
          <div className="ae-apply__layout">
            <div>
              {rejected && (
                <div role="status" className="ae-apply__outcome">
                  <p>{ct('commerce.trade.rejected', { businessName: rejected.businessName })}</p>
                  {rejected.reason && <p>{ct('commerce.trade.rejectedReason', { reason: rejected.reason })}</p>}
                  <p className="ae-apply__muted">{ct('commerce.trade.applyAgain')}</p>
                </div>
              )}
              {form}
            </div>
            {aside}
          </div>
        )}
      </div>
    </BlockRoot>
  );
}

/**
 * A field's name on this page. The catalogue is keyed by the draft's own field names, so a
 * field added to the platform's draft shows its key here rather than silently vanishing from
 * a "these are still needed" sentence.
 */
function fieldName(t: (key: string) => string, field: TradeDraftField): string {
  return t(`tradeApply.fields.${field}`);
}

/** One labelled control, with its hint: the mockup's `.field`. */
function Field({
  id,
  label,
  hint,
  className,
  children,
}: {
  id: string;
  label: ReactNode;
  hint?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={className ? `ae-apply__field ${className}` : 'ae-apply__field'}>
      <label htmlFor={id}>{label}</label>
      {children}
      {hint ? <p className="ae-apply__hint">{hint}</p> : null}
    </div>
  );
}

/** The mockup's `.choice-tile`: a radio with a title and an explanation. */
function Tile({
  name,
  checked,
  onChange,
  title,
  text,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  title: string;
  text?: string;
}) {
  return (
    <label className="ae-apply__tile">
      <input type="radio" name={name} checked={checked} onChange={onChange} />
      <span className="ae-apply__tile-body">
        <span className="t">{title}</span>
        {text ? <span className="d">{text}</span> : null}
      </span>
    </label>
  );
}

function ReferenceFields({
  index,
  value,
  label,
  onChange,
}: {
  index: number;
  value: TradeReferenceDraft;
  label: (field: keyof TradeReferenceDraft) => string;
  onChange: (value: TradeReferenceDraft) => void;
}) {
  const id = (field: string) => `ae-trade-reference-${index}-${field}`;
  const set = (field: keyof TradeReferenceDraft) => (text: string) => onChange({ ...value, [field]: text });
  return (
    <div className="ae-apply__grid">
      <Field id={id('company')} label={label('company')}>
        <input id={id('company')} type="text" maxLength={TRADE_FIELD_LIMITS.businessName} value={value.company} onChange={(event) => set('company')(event.target.value)} />
      </Field>
      <Field id={id('contactName')} label={label('contactName')}>
        <input id={id('contactName')} type="text" maxLength={TRADE_FIELD_LIMITS.contactName} value={value.contactName} onChange={(event) => set('contactName')(event.target.value)} />
      </Field>
      <Field id={id('email')} label={label('email')}>
        <input id={id('email')} type="email" maxLength={TRADE_FIELD_LIMITS.email} value={value.email} onChange={(event) => set('email')(event.target.value)} />
      </Field>
      <Field id={id('phone')} label={label('phone')}>
        <input id={id('phone')} type="tel" maxLength={TRADE_FIELD_LIMITS.phone} value={value.phone} onChange={(event) => set('phone')(event.target.value)} />
      </Field>
      <Field id={id('relationship')} className="ae-apply__span2" label={label('relationship')}>
        <input id={id('relationship')} type="text" maxLength={TRADE_FIELD_LIMITS.relationship} value={value.relationship} onChange={(event) => set('relationship')(event.target.value)} />
      </Field>
    </div>
  );
}

/** A tick, for the aside's "what approval changes" list. */
function Tick() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M2.5 7.5 5.5 10.5 11.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** A file's size as a person reads one. */
function kilobytes(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** A date as the shopper reads one, or the ISO day when the browser cannot format it. */
function shortDate(value: string, language: string | undefined): string {
  try {
    return new Intl.DateTimeFormat(language || 'en', { dateStyle: 'long' }).format(new Date(value));
  } catch {
    return value.slice(0, 10);
  }
}
